/* eslint react-hooks/exhaustive-deps: "error" */
import React, {
  createContext,
  FC,
  useContext,
  useMemo,
  useState,
  useReducer,
} from 'react';

import { CountryPrefix } from 'Jsx/global';
import { InitialStateResult } from 'Jsx/Utils/getBaseInitialState';

import {
  AppProps,
  Banner,
  OrderByOption,
  CategorySelected,
  Filters,
} from '../types';
import { setItemToUniqueArray } from '../utils';

interface DisableParams {
  levels?: string[];
  softwares?: string[];
}

interface AppContexValue extends AppProps {
  countryPrefix: CountryPrefix;
  topBanners: Banner[];
  currentPage: number;
  filterActions: {
    toggleLevelsOpen: () => void;
    toggleSoftwareOpen: () => void;
    toggleLanguageOpen: () => void;
    showAllCourses: () => void;
    toggleFilterDuration: (durationTag: string) => void;
    setSelectedFilterCategory: (category: CategorySelected) => void;
    toggleFilterCategory: (category: CategorySelected) => void;
    toggleFilterSubcategory: (subItemId: number) => void;
    toggleFilterSoftware: (subItemId: number) => void;
    toggleFilterLevel: (subItemId: number) => void;
    toggleFilterLanguage: (subItemId: number) => void;
    disableFiltersSoftwareLevels: (params: DisableParams) => void;
    clearSoftwareFilters: () => void;
    clearLevelFilters: () => void;
    clearAllFilters: () => void;
    clearMobileFilters: () => void;
    // Mobile filters
    setSelectionLanguage: (itemId: number) => void;
    setSelectionLevel: (itemId: number) => void;
    setSelectionSoftware: (itemId: number) => void;
    setSelectionDuration: (durationTag: string) => void;
  };
  orderByActions: {
    selectOrderBy: (option: OrderByOption) => void;
    setDefaultOrderBy: () => void;
  };
  setCurrentPage: (page: number) => void;
}

const AppContext = createContext<AppContexValue | undefined>(undefined);

type FilterActions =
  | { type: 'TOGGLE_LEVEL_OPEN'; payload?: never }
  | { type: 'TOGGLE_SOFTWARE_OPEN'; payload?: never }
  | { type: 'TOGGLE_LANGUAGE_OPEN'; payload?: never }
  | { type: 'SHOW_ALL_COURSES'; payload?: never }
  | { type: 'SET_SELECTED_FILTER_DURATION'; payload: string }
  | { type: 'SET_SELECTED_FILTER_CATEGORY'; payload: CategorySelected }
  | { type: 'TOGGLE_FILTER_CATEGORY'; payload: CategorySelected }
  | { type: 'TOGGLE_FILTER_SUBCATEGORY'; payload: number }
  | { type: 'TOGGLE_FILTER_SOFTWARE'; payload: number }
  | { type: 'TOGGLE_FILTER_LEVEL'; payload: number }
  | { type: 'TOGGLE_FILTER_LANGUAGE'; payload: number }
  | { type: 'CLEAR_SOFTWARE_FILTERS'; payload?: never }
  | { type: 'CLEAR_LEVEL_FILTERS'; payload?: never }
  | { type: 'CLEAR_ALL_FILTERS'; payload?: never }
  | { type: 'DISABLE_FILTER_LEVEL'; payload?: string[] }
  | { type: 'DISABLE_FILTER_SOFTWARE'; payload?: string[] }
  | { type: 'SET_SELECTION_LANGUAGE'; payload: number }
  | { type: 'SET_SELECTION_LEVEL'; payload: number }
  | { type: 'SET_SELECTION_SOFTWARE'; payload: number }
  | { type: 'SET_SELECTION_DURATION'; payload: string }
  | { type: 'CLEAR_MOBILE_FILTERS'; payload?: never };

const filterReducer = (state: Filters, action: FilterActions): Filters => {
  // eslint-disable-next-line default-case
  switch (action.type) {
    case 'TOGGLE_LEVEL_OPEN':
      return {
        ...state,
        levels: { ...state.levels, open: !state.levels.open },
      };
    case 'TOGGLE_SOFTWARE_OPEN':
      return {
        ...state,
        softwares: { ...state.softwares, open: !state.softwares.open },
      };
    case 'TOGGLE_LANGUAGE_OPEN':
      return {
        ...state,
        languages: { ...state.languages, open: !state.languages.open },
      };
    case 'SHOW_ALL_COURSES':
      return {
        ...state,
        allCourses: true,
        showRecommendedForMeCourses: false,
      };
    case 'SET_SELECTED_FILTER_DURATION': {
      const newSelectedTags = setItemToUniqueArray(
        state.duration.selectedTags,
        action.payload,
      );

      return {
        ...state,
        duration: {
          ...state.duration,
          selectedTags: newSelectedTags.slice(),
        },
        mobileSelectionDuration: {
          ...state.mobileSelectionDuration,
          selected: newSelectedTags.slice(),
          edited: [],
          dirty: false,
        },
      };
    }
    case 'SET_SELECTED_FILTER_CATEGORY':
      return {
        ...state,
        allCourses: false,
        categoryOpen: {
          itemId: action.payload.itemId,
        },
        categorySelected: {
          ...state.categorySelected,
          itemId: action.payload.itemId,
          slug: action.payload.slug,
          subItemId: action.payload.subItemId,
        },
        softwaresSelected: {},
        levelsSelected: {},
      };
    case 'TOGGLE_FILTER_CATEGORY':
      return {
        ...state,
        allCourses: false,
        categoryOpen: {
          itemId:
            action.payload.itemId === state.categoryOpen.itemId
              ? null
              : action.payload.itemId,
        },
        categorySelected:
          typeof state.categorySelected.slug === 'string' &&
          state.categorySelected.itemId === action.payload.itemId
            ? { itemId: null, slug: null, subItemId: null }
            : {
                ...state.categorySelected,
                itemId: action.payload.itemId,
                slug: action.payload.slug,
                subItemId: action.payload.subItemId,
              },
        softwares: { ...state.softwares, open: false },
        levels: { ...state.levels, open: false },
        softwaresSelected: {},
        levelsSelected: {},
      };
    case 'TOGGLE_FILTER_SUBCATEGORY':
      return {
        ...state,
        categorySelected: {
          ...state.categorySelected,
          slug:
            state.categories[state.categorySelected.itemId!]?.subItems[
              action.payload
            ]?.slug || state.categorySelected.slug,
          subItemId:
            state.categorySelected.subItemId === action.payload
              ? 0
              : action.payload,
        },
      };
    case 'TOGGLE_FILTER_SOFTWARE':
      return {
        ...state,
        softwaresSelected: {
          ...state.softwaresSelected,
          [action.payload]: state.softwaresSelected[action.payload]
            ? null
            : state.softwares.subItems[action.payload],
        },
      };
    case 'TOGGLE_FILTER_LEVEL':
      return {
        ...state,
        levelsSelected: {
          ...state.levelsSelected,
          [action.payload]: state.levelsSelected[action.payload]
            ? null
            : state.levels.subItems[action.payload],
        },
      };
    case 'TOGGLE_FILTER_LANGUAGE':
      return {
        ...state,
        selectedLanguages: {
          ...state.selectedLanguages,
          [action.payload]: state.selectedLanguages[action.payload]
            ? null
            : state.languages.subItems[action.payload],
        },
      };
    case 'CLEAR_SOFTWARE_FILTERS':
      return { ...state, softwaresSelected: {} };
    case 'CLEAR_LEVEL_FILTERS':
      return { ...state, levelsSelected: {} };
    case 'CLEAR_ALL_FILTERS':
      return {
        ...state,
        categorySelected: {
          itemId: null,
          subItemId: null,
          slug: null,
        },
        categoryOpen: {
          itemId: null,
        },
        levelsSelected: {},
        softwaresSelected: {},
        selectedLanguages: {},
        duration: {
          selectedTags: [],
        },
        mobileSelectionLanguages: {
          selected: state.mobileSelectionLanguages.selected,
          edited: [],
          dirty: false,
        },
        mobileSelectionLevels: {
          selected: state.mobileSelectionLevels.selected,
          edited: [],
          dirty: false,
        },
        mobileSelectionSoftwares: {
          selected: state.mobileSelectionSoftwares.selected,
          edited: [],
          dirty: false,
        },
        mobileSelectionDuration: {
          selected: state.mobileSelectionDuration.selected,
          edited: [],
          dirty: false,
        },
        languages: state.defaultLanguages,
        levels: state.defaultLevels,
        softwares: state.defaultSoftwares,
        showRecommendedForMeCourses: false,
      } as Filters;
    case 'CLEAR_MOBILE_FILTERS':
      return {
        ...state,
        mobileSelectionLanguages: {
          selected: state.mobileSelectionLanguages.selected,
          edited: [],
          dirty: false,
        },
        mobileSelectionLevels: {
          selected: state.mobileSelectionLevels.selected,
          edited: [],
          dirty: false,
        },
        mobileSelectionSoftwares: {
          selected: state.mobileSelectionSoftwares.selected,
          edited: [],
          dirty: false,
        },
        mobileSelectionDuration: {
          selected: state.duration.selectedTags.slice(),
          edited: [],
          dirty: false,
        },
      };
    case 'DISABLE_FILTER_LEVEL':
      if (action.payload) {
        return {
          ...state,
          levels: {
            ...state.levels,
            subItems: Object.entries(state.levels.subItems).reduce(
              (acc, [key, val]) => ({
                ...acc,
                [key]: {
                  ...val,
                  disabled:
                    !action.payload!.includes(val.slug) &&
                    !state.levelsSelected[val.id],
                },
              }),
              {},
            ),
          },
        };
      }
      return {
        ...state,
        levels: {
          ...state.defaultLevels,
          // Keep open state
          open: state.levels.open,
        },
      };
    case 'DISABLE_FILTER_SOFTWARE':
      if (action.payload) {
        return {
          ...state,
          softwares: {
            ...state.softwares,
            subItems: Object.keys(state.softwares.subItems).reduce(
              (acc, curr) => {
                const val = acc[curr]!;
                const isIncluded = action.payload!.includes(val.slug);

                return {
                  ...acc,
                  [curr]: {
                    ...val,
                    disabled: !isIncluded && !state.softwaresSelected[val.id],
                  },
                };
              },
              state.softwares.subItems,
            ),
          },
        };
      }
      return {
        ...state,
        softwares: {
          ...state.defaultSoftwares,
          // Keep open state
          open: state.softwares.open,
        },
      };
    case 'SET_SELECTION_LANGUAGE': {
      return {
        ...state,
        mobileSelectionLanguages: {
          selected: setItemToUniqueArray(
            state.mobileSelectionLanguages.selected,
            action.payload,
          ),
          edited: setItemToUniqueArray(
            state.mobileSelectionLanguages.edited,
            action.payload,
          ),
          dirty: true,
        },
      };
    }
    case 'SET_SELECTION_LEVEL': {
      return {
        ...state,
        mobileSelectionLevels: {
          selected: setItemToUniqueArray(
            state.mobileSelectionLevels.selected,
            action.payload,
          ),
          edited: setItemToUniqueArray(
            state.mobileSelectionLevels.edited,
            action.payload,
          ),
          dirty: true,
        },
      };
    }
    case 'SET_SELECTION_SOFTWARE': {
      return {
        ...state,
        mobileSelectionSoftwares: {
          selected: setItemToUniqueArray(
            state.mobileSelectionSoftwares.selected,
            action.payload,
          ),
          edited: setItemToUniqueArray(
            state.mobileSelectionSoftwares.edited,
            action.payload,
          ),
          dirty: true,
        },
      };
    }
    case 'SET_SELECTION_DURATION': {
      return {
        ...state,
        mobileSelectionDuration: {
          selected: setItemToUniqueArray(
            state.mobileSelectionDuration.selected,
            action.payload,
          ),
          edited: setItemToUniqueArray(
            state.mobileSelectionDuration.edited,
            action.payload,
          ),
          dirty: true,
        },
      };
    }
  }
};

export interface AppProviderProps {
  value: InitialStateResult<AppProps>;
}

const AppProvider: FC<AppProviderProps> = ({ value, children }) => {
  const { banners, categoriesBanners } = value;

  const [filters, setFilters] = useReducer(filterReducer, value.filters);
  const [orderBy, setOrderBy] = useState(value.orderBy);
  const [currentPage, setCurrentPage] = useState(1);

  const { categorySelected: categoryInfo } = filters;

  const topBanners = useMemo(() => {
    if (categoryInfo.slug) {
      const categoryBanners = categoriesBanners[categoryInfo.slug];

      if (categoryBanners && categoryBanners.length > 0) {
        const selectedBanners = categoriesBanners[categoryInfo.slug];

        if (selectedBanners) {
          return selectedBanners;
        }
      }
    }

    return banners;
  }, [banners, categoriesBanners, categoryInfo.slug]);

  const filterActions = useMemo(
    () => ({
      toggleLevelsOpen: () => setFilters({ type: 'TOGGLE_LEVEL_OPEN' }),
      toggleSoftwareOpen: () => setFilters({ type: 'TOGGLE_SOFTWARE_OPEN' }),
      toggleLanguageOpen: () => setFilters({ type: 'TOGGLE_LANGUAGE_OPEN' }),
      showAllCourses: () => setFilters({ type: 'SHOW_ALL_COURSES' }),
      toggleFilterDuration: (durationTag: string) =>
        setFilters({
          type: 'SET_SELECTED_FILTER_DURATION',
          payload: durationTag,
        }),
      setSelectedFilterCategory: (payload: CategorySelected) =>
        setFilters({ type: 'SET_SELECTED_FILTER_CATEGORY', payload }),
      toggleFilterCategory: (payload: CategorySelected) => {
        setFilters({ type: 'TOGGLE_FILTER_CATEGORY', payload });
      },
      toggleFilterSubcategory: (payload: number) => {
        setFilters({ type: 'TOGGLE_FILTER_SUBCATEGORY', payload });
      },
      toggleFilterSoftware: (payload: number) => {
        setFilters({ type: 'TOGGLE_FILTER_SOFTWARE', payload });
      },
      toggleFilterLevel: (payload: number) => {
        setFilters({ type: 'TOGGLE_FILTER_LEVEL', payload });
      },
      toggleFilterLanguage: (payload: number) => {
        setFilters({ type: 'TOGGLE_FILTER_LANGUAGE', payload });
      },
      disableFiltersSoftwareLevels: ({ levels, softwares }: DisableParams) => {
        setFilters({ type: 'DISABLE_FILTER_LEVEL', payload: levels });
        setFilters({ type: 'DISABLE_FILTER_SOFTWARE', payload: softwares });
      },
      clearSoftwareFilters: () =>
        setFilters({ type: 'CLEAR_SOFTWARE_FILTERS' }),
      clearLevelFilters: () => setFilters({ type: 'CLEAR_LEVEL_FILTERS' }),
      clearAllFilters: () => setFilters({ type: 'CLEAR_ALL_FILTERS' }),
      setSelectionLanguage: (itemId: number) =>
        setFilters({ type: 'SET_SELECTION_LANGUAGE', payload: itemId }),
      setSelectionLevel: (itemId: number) =>
        setFilters({ type: 'SET_SELECTION_LEVEL', payload: itemId }),
      setSelectionSoftware: (itemId: number) =>
        setFilters({ type: 'SET_SELECTION_SOFTWARE', payload: itemId }),
      setSelectionDuration: (durationTag: string) =>
        setFilters({ type: 'SET_SELECTION_DURATION', payload: durationTag }),
      clearMobileFilters: () => setFilters({ type: 'CLEAR_MOBILE_FILTERS' }),
    }),
    [],
  );

  const orderByActions = useMemo(
    () => ({
      selectOrderBy: (option: OrderByOption) => {
        setOrderBy(prev => ({
          ...prev,
          itemSelected: option,
        }));
      },
      setDefaultOrderBy: () => {
        setOrderBy(prev => ({
          ...prev,
          itemSelected: prev.defaultOrderBy,
        }));
      },
    }),
    [],
  );

  const contextValue: AppContexValue = {
    ...value,
    filters,
    orderBy,
    currentPage,
    topBanners,
    filterActions,
    orderByActions,
    setCurrentPage,
  };

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
};

export function useAppContext() {
  const ctx = useContext(AppContext);

  if (typeof ctx === 'undefined') {
    throw new Error(
      'Catalog: useAppContext must be used within an AppProvider',
    );
  }

  return ctx;
}

export default AppProvider;
